import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Utility } from 'helpers/UtilityHelpers';
import { Code } from 'constants/codes';
import apiLogin from "services/loginInterceptors";
import { Location } from 'models/LocationModel';
import { Schedule } from 'models/ScheduleModel';
import { Files } from 'helpers/FileHelpers';
import { Hooks } from 'hooks/commonHooks';
import { MediaList } from 'models/BaseModel';

import '../../assets/styles/Appointment.css';
import {type AppointmentOnlineData, type AppointmentFormErrors, INITIAL_APPOINTMENT, validateForm} from 'models/AppointmentOnlineData';
import { Appointment } from 'models/AppointmentModel';
import ReCAPTCHA from 'react-google-recaptcha';
import { Control } from 'components/common/ControlManager';


interface ClinicHeaderProps { name: string; address: string; themeColor: string; logoUrl?: string | null; }
const ClinicHeader: React.FC<ClinicHeaderProps> = ({ name, address, themeColor, logoUrl }) => (
  <div className="clinic-header" style={{backgroundColor:`#${themeColor}`}}>
    <div className="clinic-logo">
      {logoUrl ? (
        <img src={logoUrl} alt="Clinic Logo" />
      ) : (
        <div className="clinic-name">{name}</div>
      )}
    </div>
    <div className="clinic-tagline">{address}</div>
  </div>
);

interface SuccessPanelProps { firstName: string; }
const SuccessPanel: React.FC<SuccessPanelProps> = ({ firstName }) => (
  <div className="success-panel">
    <div className="success-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>Appointment Request Submitted!</h3>
    <p>
      Thank you {firstName}<br/> Your appointment request has been received. You may receive a confirmation call soon.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AppointmentOnline: React.FC = () => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId]     = useState<string>(paramKeyId ?? '');
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [databaseId, setDatabaseId] = useState<Number>(10);
  const [companyId, setCompanyId] = useState<Number>(2);
  const [companyPath, setCompanyPath] = useState<string>('');
  const [locationId, setLocationId] = useState<Number>(2);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyAddress, setCompanyAddress] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string | null >(null);
  const [locationPath, setLocationPath] = useState<string>(keyId);
  
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [locationOptions, setLocationOptions] = Hooks.Form.Options();

  const [form, setForm] = useState<AppointmentOnlineData>(INITIAL_APPOINTMENT);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<AppointmentFormErrors>({});

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const [headerThemeColor, setHeaderThemeColor] = useState<string>('0d3b5e');
  const [footerThemeColor, setFooterThemeColor] = useState<string>('666');
  const [buttonThemeColor, setButtonThemeColor] = useState<string>('0d3b5e');
  const [textThemeColor, setTextThemeColor] = useState<string>('008716');
  const [stepsThemeColor, setStepsThemeColor] = useState<string>('');
  const [dateThemeColor, setDateThemeColor] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<number | null>(null);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<string>('');
  const [selectedScheduleStartTime, setSelectedScheduleStartTime] = useState<string>('');
  const [selectedScheduleEndTime, setSelectedScheduleEndTime] = useState<string>('');
  const [selectedAppointmentStartTime, setSelectedAppointmentStartTime] = useState<string>('');
  const [selectedAppointmentEndTime, setSelectedAppointmentEndTime] = useState<string>('');

  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [availableDales, setAvailableDates] = useState<Schedule[]>([]);
  const [bookedSlots, setBookedSlots] = useState<Appointment[]>([]);
  const [availableSlots, setAvailableSlots] = useState<Appointment[]>([]);
  
  const [slotLength, setSlotLength] = useState<number>(30);
  const [showTermsOfUse, setShowTermsOfUse] = useState<boolean>(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState<boolean>(false);

  const yearOptions = Array.from({ length: 2026 - 1920 + 1 }, (_, i) => (2026 - i).toString());
  const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  const [dobYear, setDobYear] = useState<string>('');
  const [dobMonth, setDobMonth] = useState<string>('');
  const [dobDay, setDobDay] = useState<string>('');

  interface ThemeColor {
    header?: string;
    footer?: string;
    button?: string;
    text?: string;
    steps?: string;
    date?: string;
  }

  const bookableDates = new Set(availableDales.map(item => {
    const date = item.schedule_start_date;
    const dateStr = typeof date === 'string' ? date : date?.toISOString() || '';
    return dateStr.split('T')[0];
  }));

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      appointment_dob: dobYear && dobMonth && dobDay
        ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
        : ''
    }));
  }, [dobYear, dobMonth, dobDay]);
 
  const update = <K extends keyof AppointmentOnlineData>(key: K, value: AppointmentOnlineData[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };
  
  const getMonthLabel = (date: Date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const buildCalendar = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const dayOffset = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const cells = [] as Array<{label: string; date: string | null; isToday: boolean}>;

    for (let i = 0; i < dayOffset; i += 1) {
      cells.push({ label: '', date: null, isToday: false });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const current = new Date(year, month, day);
      const isoDate = formatDate(current);
      const today = formatDate(new Date()) === isoDate;
      cells.push({ label: String(day), date: isoDate, isToday: today });
    }

    while (cells.length < 42) {
      cells.push({ label: '', date: null, isToday: false });
    }

    return cells;
  };

  const calendarCells = buildCalendar(calendarMonth);

  const handleSegmentSelect = (value: number) => {
    setSelectedSegment(value);
    setSelectedLocation(null);
    setSelectedScheduleDate('');
    setSelectedTimeSlot('');
  };

  const handleLocationSelect = (value: number) => {
    setSelectedLocation(value);
    setLocationId(value);
    update('location_id', value as AppointmentOnlineData['location_id']);
    setSelectedScheduleDate('');
    setSelectedTimeSlot('');
  };

  const selectedSegmentLabel = segmentOptions.find(option => option.value === selectedSegment)?.label || '';
  const selectedLocationLabel = locationOptions.find(option => option.value === selectedLocation)?.label || '';

  const steps = [
    `${selectedSegmentLabel ? selectedSegmentLabel : 'Select Exam'}`,
    `${selectedLocationLabel ? selectedLocationLabel : 'Select Location'}`,
    `${selectedScheduleDate ? selectedScheduleDate : 'Select date'}`,
    `${selectedTimeSlot ? selectedTimeSlot.replace("-","")  : 'Select time'}`,
    'Patient details',
    'Finalize',
  ];

  const currentStep = submitted
    ? 6
    : selectedTimeSlot
      ? 5
      : selectedScheduleDate
        ? 4
        : selectedLocation !== null
          ? 3
          : selectedSegment !== null
            ? 2
            : 1;

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex >= currentStep) return;

    switch (stepIndex) {
      case 1:
        setSelectedSegment(null);
        setSelectedLocation(null);
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 2:
        setSelectedLocation(null);
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 3:
        setSelectedScheduleDate('');
        setSelectedTimeSlot('');
        break;
      case 4:
        setSelectedTimeSlot('');
        break;
      default:
        break;
    }
  };

  const handleDateSelection = (date: string | null) => {
    if (!date) return;
    setSelectedScheduleDate(date);

    const selectedSlot = availableDales.find(item => {
      const scheduleDate = typeof item.schedule_start_date === 'string'
        ? item.schedule_start_date
        : item.schedule_start_date instanceof Date
          ? item.schedule_start_date.toISOString()
          : '';
       return scheduleDate.split('T')[0] === date;
    });

    if (selectedSlot) {
      const startTime = selectedSlot.schedule_start_time || '';
      const endTime = selectedSlot.schedule_end_time || '';
      const doctorId = selectedSlot.doctor_id || null;
      setSelectedScheduleStartTime(startTime);
      setSelectedScheduleEndTime(endTime);
      setSelectedDoctor(doctorId);
      //console.log('Selected date:', date, 'Start time:', startTime, 'End time:', endTime);
    } else {
      setSelectedScheduleStartTime('');
      setSelectedScheduleEndTime('');
      setSelectedDoctor(null);
    }
  };

    const handleTimeSelection = (slot: Appointment) => {
    setSelectedTimeSlot(`${formatTime(slot.appointment_start_time)} - ${formatTime(slot.appointment_end_time)}`);
    setSelectedAppointmentStartTime(slot.appointment_start_time || '');
    setSelectedAppointmentEndTime(slot.appointment_end_time || '');
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const [validationErrors] = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setToast('Please fill in all required fields before submitting.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    // Anti-spam checks
    if (!recaptchaToken) {
      setToast('Please complete the reCAPTCHA verification.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    if (form.website.trim()) {
      // Honeypot field filled, likely spam
      console.warn('Honeypot field filled, potential spam detected');
      setToast('There was a problem submitting your appointment. Please try again.');
      setTimeout(() => setToast(null), 4000);
      return;
    }

    try {

      let commentData = 'Online booking\n';
      commentData += form.appointment_first_name ? `Name: ${form.appointment_first_name} ${form.appointment_last_name}\n` : '';
      commentData += form.appointment_phone ? `Phone: ${form.appointment_phone}\n` : '';
      commentData += form.appointment_email ? `Email: ${form.appointment_email}\n` : '';
      commentData += form.appointment_health_card ? `Health Card: ${form.appointment_health_card}\n` : '';
      commentData += form.appointment_dob ? `Date of Birth: ${form.appointment_dob}\n` : '';

      //console.log('Submitting appointment with data:', commentData);

      const response = await fetch(`${Code.Path.SERVER}appointment/online/${databaseId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          //...form,
          company_id: companyId,
          location_id: locationId,
          branch_id: locationId,
          client_id: 4787, // online booking client ID
          status_id: 5, // New entry status
          doctor_id: selectedDoctor,
          appointment_date: Utility.Date.Current(),
          appointment_start_date: selectedScheduleDate,
          appointment_end_date: selectedScheduleDate,
          appointment_start_time: selectedAppointmentStartTime,
          appointment_end_time: selectedAppointmentEndTime,
          appointment_comment: commentData,
          recaptcha_token: recaptchaToken,
          keyId,
        }),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Network error during submission:', err);
      setToast('There was a problem submitting your appointment. Please try again.');
      setTimeout(() => setToast(null), 4000);
    }
  };

 useEffect(() => {
  const fetchLocation = async () => {
    document.body.style.backgroundImage = "none";
    if (locationPath) {
      try {
        const url = `${Code.Path.SERVER}auth/location/${locationPath}`;
        const response = await apiLogin.get(url);
        console.clear();
        //console.log("response>>>>>", response);
        if (response) {
          const result = response.data;
          //console.log("result>>>>>", result.data);
          if (result.status === "success" && result.code === 200) {
            const location = result.data as Location;
            if (location) {
              console.log("location:",location);
              setDatabaseId(location.database_id || 0);
              setCompanyId(location.company_id || 0);
              setCompanyPath(location.company_path || '');
              setLocationId(location.location_id || 0);
              setCompanyName(location.company_legal_name || '');
              setCompanyAddress(location.location_title?.replace(/\r?\n/g, "") || '');

              console.log("Location:", location);

              const themeColor = location.theme_color;
              if (themeColor) {

          
                const colors: ThemeColor = JSON.parse(themeColor);
          
                console.log("Theme colors:", colors);

                setHeaderThemeColor(colors.header || '0d3b5e');
                setFooterThemeColor(colors.footer || '666');
                setButtonThemeColor(colors.button || '0d3b5e');
                setTextThemeColor(colors.text || '008716');
                setStepsThemeColor(colors.steps || '008716');
                setDateThemeColor(colors.date || '008716');
              }

              const fileList = location.file_list;

              console.log("Location fileList:", fileList);

              document.title = `Appointment Book - ${location.company_legal_name} - Powered by NextVizon`;
              let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
              if (!metaDesc) {
                metaDesc = document.createElement("meta");
                metaDesc.name = "description";
                document.head.appendChild(metaDesc);
              }
              metaDesc.setAttribute("content", `Book your appointment online anytime by selecting your preferred exam type, location, date, and time at ${location.company_legal_name}.`);
              
              let metaRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
              if (!metaRobots) {
                metaRobots = document.createElement("meta");
                metaRobots.name = "robots";
                document.head.appendChild(metaRobots);
              }
              metaRobots.setAttribute("content", "index, follow");  
              
              if (fileList && location.company_path) {
                const fnames: MediaList = JSON.parse(fileList);
                const imageName = fnames.logo;
                const companyPath = location.company_path;
                console.log("Location company_path:", companyPath);
                console.log("Location imageName:", imageName);

                if(imageName){
                  const logo = await Files.ImageAPI.FetchWT(companyPath, imageName);
                  console.log("Fetched logo URL:", logo);
                  setLogoUrl(logo);
                }
                
              }

            }
          }
        }
      } catch (error) {
        console.error("fetchLocation error >>>>>", error);
        setCompanyId(11);
        setLocationId(13);
        setCompanyName(Utility.Convert.TitleCase(locationPath?.replaceAll("-"," ") || ''));
        setCompanyAddress('');

        setHeaderThemeColor('0d3b5e');
        setFooterThemeColor('666');
        setButtonThemeColor('0d3b5e');
      }
    }
  };

  fetchLocation();
}, [locationPath]);

useEffect(() => {
  if(companyPath){
    Files.Json.Http("segment", Code.Option.Common.NOTHING, setSegmentOptions, setError, companyPath);
  }
}, [companyPath]);

//bypassing auto selection of single segment
// useEffect(() => {
//   if(segmentOptions && segmentOptions.length == 1){
//     setSelectedSegment(segmentOptions[0].value)
//   }
// }, [segmentOptions]);

//bypassing auto selection of single location
// useEffect(() => {
//   if(locationOptions && locationOptions.length == 1){
//     setSelectedLocation(locationOptions[0].value)
//   }
// }, [locationOptions]);

useEffect(() => {
  if(selectedSegment && selectedSegment>9){
    Files.Json.Http(`location${selectedSegment}`, Code.Option.Common.NOTHING, setLocationOptions, setError, companyPath);
  }
}, [selectedSegment]);

useEffect(() => {
  if(selectedLocation && selectedLocation>9 && selectedSegment && selectedSegment>9){
    const fetchScheduleData = async () => {
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth() + 1;
      try {
        const response = await fetch(`${Code.Path.SERVER}custom/schedule/${databaseId}/${companyId}/${selectedLocation}/${selectedSegment}/${year}/${month}`);
        if (response.ok) {
          const data = await response.json();
          //console.log('Schedule data fetched:', data);
          if (data && data.status === 'success' && data.code === 200) {
            setAvailableDates(data.data || []);
          } else {
            console.error('API returned non-success status:', data);
          }
        } else {
          console.error('Failed to fetch schedule data:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching schedule data:', error);
      }
    };

    fetchScheduleData();
  }
}, [selectedLocation, calendarMonth]);

useEffect(() => {
  if(selectedLocation && selectedDoctor && selectedScheduleDate){
    const fetchAppoitmentData = async () => {
      const date = new Date(selectedScheduleDate).toISOString().split('T')[0];
      try {
        //console.log("selectedScheduleDate>>>>>", selectedScheduleDate);
        const response = await fetch(`${Code.Path.SERVER}custom/appointment/${databaseId}/${companyId}/${selectedLocation}/${selectedDoctor}/${date}`);
        if (response.ok) {
          const data = await response.json();
          //console.log('Appointment data fetched:', data);
          if (data && data.status === 'success' && data.code === 200) {
            setBookedSlots(data.data || []);
          } else {
            console.error('API returned non-success status:', data);
          }
        } else {
          console.error('Failed to fetch appointment data:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching appointment data:', error);
      }
    };

    fetchAppoitmentData();
  }
}, [selectedLocation, selectedDoctor, selectedScheduleDate]);

useEffect(() => {

  console.log('all Booked appointments for selected date:', bookedSlots);
  if (bookedSlots.length >= 0 && selectedScheduleDate && selectedScheduleStartTime && selectedScheduleEndTime) {
  
    
    //const selectedStart = new Date(`${selectedScheduleDate}T${selectedScheduleStartTime}`);
    //const selectedEnd = new Date(`${selectedScheduleDate}T${selectedScheduleEndTime}`);

   const availableSlots = getAvailableTimeSlots(
      selectedScheduleDate,
      selectedScheduleStartTime,
      selectedScheduleEndTime,
      bookedSlots
    );
  
    setAvailableSlots(availableSlots);
  }
}, [bookedSlots,selectedScheduleDate,selectedScheduleStartTime,selectedScheduleEndTime]);
 
/**
 * Converts time string (HH:MM:SS or HH:MM) to minutes since midnight
 */
function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}
 
/**
 * Converts minutes since midnight to time string (HH:MM:SS)
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
}

const formatTime = (time: string | undefined): string => {
  if (!time) return '-';
  
  const date = new Date(`2000-01-01 ${time}`);
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
 

function getAvailableTimeSlots(
  selectedScheduleDate: string | Date,
  selectedScheduleStartTime: string,
  selectedScheduleEndTime: string,
  alreadyBooked: Appointment[] = []
): Appointment[] {
  // Convert date to comparable format
  const dateStr =
    selectedScheduleDate instanceof Date
      ? selectedScheduleDate.toISOString().split("T")[0]
      : selectedScheduleDate;
 
  // Convert times to minutes for easier calculation
  const officeStartMinutes = timeToMinutes(selectedScheduleStartTime);
  const officeEndMinutes = timeToMinutes(selectedScheduleEndTime);
 
  // Generate all 30-minute slots for office hours
  const allSlots: Appointment[] = [];
  for (let minutes = officeStartMinutes; minutes < officeEndMinutes; minutes += slotLength) {
    const startTime = minutesToTime(minutes);
    const endTime = minutesToTime(minutes + slotLength);
    allSlots.push({ appointment_start_time: startTime, appointment_end_time: endTime });
  }
 
  // Extract booked slots for this specific date
  const bookedOnThisDate = alreadyBooked.filter((appointment) => {
    const appointmentDate =
      appointment.appointment_start_date instanceof Date
        ? appointment.appointment_start_date.toISOString().split("T")[0]
        : String(appointment.appointment_start_date).split("T")[0];
    return appointmentDate === dateStr;
  });
 
  // Convert booked slots to minutes for comparison
  const bookedSlots = bookedOnThisDate.map((appointment) => ({
    startMinutes: timeToMinutes(appointment.appointment_start_time || "00:00"),
    endMinutes: timeToMinutes(appointment.appointment_end_time || "00:00"),
  }));
 
  // Filter out booked slots
  const availableSlots = allSlots.filter((slot) => {
    const slotStartMinutes = timeToMinutes(slot.appointment_start_time  || "00:00");
    const slotEndMinutes = timeToMinutes(slot.appointment_end_time || "00:00");
 
    // Check if this slot overlaps with any booked slot
    const isBooked = bookedSlots.some(
      (booked) =>
        slotStartMinutes < booked.endMinutes && slotEndMinutes > booked.startMinutes
    );
 
    return !isBooked;
  });
 
  return availableSlots;
}

useEffect(() => {
  if (paramKeyId) setKeyId(paramKeyId);
}, [paramKeyId]);


return (
    <div className="page-wrapper">
      <ClinicHeader name={companyName} address={companyAddress} themeColor={headerThemeColor} logoUrl={logoUrl} />

      <div className="form-card">
        <div className="form-title-bar" style={{backgroundColor:`#${footerThemeColor}`}}>
          <h2>Appointment Booking</h2>
          {/* <span className="badge">Confidential</span> */}
        </div>

        <div className="stepper">
          {steps.map((label, index) => {
            const stepIndex = index + 1;
            const isCompleted = stepIndex < currentStep;
            const isActive = stepIndex === currentStep;
            return (
              <div
                className={['step-item', isCompleted ? 'completed' : '', isActive ? 'active' : '', stepIndex < currentStep ? 'clickable' : ''].join(' ').trim()}
                key={label}
                title={label}
                onClick={() => handleStepClick(stepIndex)}
              >
                <div className="step-marker" style={{backgroundColor: isCompleted || isActive ? `#${stepsThemeColor}` : '#ccc'}}>
                  {isCompleted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : stepIndex}
                </div>
                <div className="step-label" style={{color: isCompleted || isActive ? `#${stepsThemeColor}` : '#666'}}>
                  {label}
                </div>
                {index < steps.length - 1 && <div className={['step-connector', isCompleted ? 'completed' : ''].join(' ').trim()} />}
              </div>
            );
          })}
        </div>

        <div className="form-body">
          {submitted ? (
            <SuccessPanel firstName={form.appointment_first_name} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              
              {/* ══ 1. Appointment type ══════════════════════════════════════ */}
              {selectedSegment === null && (
                <>
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Select an appointment type</div>
                  </div>
                  <div className="field-button-frame">
                    {segmentOptions.length > 0 ? (
                      segmentOptions.map((option) => (
                        <button
                          type="button"
                          className={['field-btn', selectedSegment === option.value ? 'selected' : ''].join(' ').trim()}
                          style={{backgroundColor:`#${buttonThemeColor}`}}
                          key={option.value}
                          onClick={() => handleSegmentSelect(option.value as number)}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <div className="field-note">Loading appointment types...</div>
                    )}
                  </div>
                </>
              )}

              {/* ══ 2. Location ══════════════════════════════════════ */}
              {selectedSegment !== null && selectedLocation === null && (
                <>
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Select a location</div>
                  </div>
                  <div className="field-button-frame">
                    {locationOptions.length > 0 ? (
                      locationOptions.map((option) => (
                        <button
                          type="button"
                          className={['field-btn', selectedLocation === option.value ? 'selected' : ''].join(' ').trim()}
                          style={{backgroundColor:`#${buttonThemeColor}`}}
                          key={option.value}
                          onClick={() => handleLocationSelect(option.value as number)}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <div className="field-note">Loading available locations...</div>
                    )}
                  </div>
                </>
              )}

              {/* ══ 3. Calendar ══════════════════════════════════════ */}
              {selectedLocation !== null && selectedScheduleDate === '' && (
                <>
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Choose an appointment date</div>
                  </div>
                  <div className="calendar-panel">
                    <div className="calendar-header">
                      <button
                        type="button"
                        className="calendar-nav"
                        style={{backgroundColor: `#${buttonThemeColor}`}}
                        onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                      >
                        ‹
                      </button>
                      <div className="calendar-title">{getMonthLabel(calendarMonth)}</div>
                      <button
                        type="button"
                        className="calendar-nav"
                        style={{backgroundColor: `#${buttonThemeColor}`}}
                        onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                      >
                        ›
                      </button>
                    </div>
                    <div className="calendar-weekdays">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday) => (
                        <div key={weekday} className="calendar-weekday">{weekday}</div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {calendarCells.map((cell, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={['calendar-day', !cell.date ? 'calendar-day--empty' : '', selectedScheduleDate === cell.date ? 'selected' : '', cell.isToday ? 'today' : '', cell.date && bookableDates.has(cell.date) ? 'available' : '', cell.date && cell.date < formatDate(new Date()) ? 'field-past ' : ''].join(' ').trim()}
                          style={cell.date && bookableDates.has(cell.date) ? { backgroundColor: `#${dateThemeColor}` } : undefined}
                          disabled={!cell.date || !bookableDates.has(cell.date) || (cell.date ? cell.date < formatDate(new Date()) : false)}
                          onClick={() => handleDateSelection(cell.date)}
                        >
                          {cell.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {selectedScheduleDate && (
                    <div className="selection-summary">Selected date: {selectedScheduleDate}</div>
                  )}
                </>
              )}

              {/* ══ 4. Time Slots available here ══════════════════════════════════════ */}
              {selectedScheduleDate !== '' && selectedTimeSlot === '' && (
                <>
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Choose a time slot</div>
                  </div>
                  <div className="field-button-frame">
                    {availableSlots.map((slot) => (
                      <button
                        key={`${slot.appointment_start_time} - ${slot.appointment_end_time}`}
                        type="button"
                        className={['field-btn'].join(' ').trim()}
                        style={{backgroundColor:`#${buttonThemeColor}`}}
                        onClick={() => handleTimeSelection(slot)}
                      >
                        {formatTime(slot.appointment_start_time)} - {formatTime(slot.appointment_end_time)}
                      </button>
                    ))}
                  </div>
                  {availableSlots.length === 0 && (
                    <div className="field-note pb8">No available time slots for the selected date.</div>
                  )}
                  <div className="change-date" onClick={() => handleStepClick(3)} style={{backgroundColor: `#${stepsThemeColor}`}}>Choose Different Date</div>
                  {selectedTimeSlot && (
                    <div className="selection-summary">Selected time slot: {selectedTimeSlot}</div>
                  )}
                </>
              )}

              {/* ══ 5. Patient details form ══════════════════════════════════════ */}
              {selectedTimeSlot && (
                <>
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Appointment Summary</div>
                  </div>
                 
                  <div className="grid-2 mb4">
                    <div className="selection-summary">
                      {selectedSegmentLabel && (<> Exam Type: <span style={{backgroundColor: `#${stepsThemeColor}`}}>{selectedSegmentLabel}</span><br /> </> )}
                      {selectedLocationLabel && (<> Location: <span style={{backgroundColor: `#${stepsThemeColor}`}}>{selectedLocationLabel}</span><br /> </> )}
                    </div>
                    <div className="selection-summary">
                      {selectedScheduleDate && (<> Date: <span style={{backgroundColor: `#${stepsThemeColor}`}}>{selectedScheduleDate}</span><br /> </> )}
                      {selectedTimeSlot && (<> Time: <span style={{backgroundColor: `#${stepsThemeColor}`}}>{selectedTimeSlot}</span><br /> </> )}
                    </div>
                  </div>
                    
                    
                  
                  <div className="section">
                    <div className="section-label" style={{color: `#${textThemeColor}`}}>Patient details</div>
                  </div>
                  <div className="field-grid">
                    <label className="field-block">
                      <span>First name <span className="clinic-req">*</span></span>
                      <input
                        type="text"
                        value={form.appointment_first_name}
                        onChange={(e) => update('appointment_first_name', e.target.value)}
                        className={errors.appointment_first_name ? 'error-border' : ''}
                      />
                    </label>
                    <label className="field-block">
                      <span>Last name</span>
                      <input
                        type="text"
                        value={form.appointment_last_name}
                        onChange={(e) => update('appointment_last_name', e.target.value)}
                        className={errors.appointment_last_name ? 'error-border' : ''}
                      />
                    </label>
                    <div className="field">
                    <label>Date of Birth <span className="clinic-req">*</span></label>
                      <div className={`dob-selects ${errors.appointment_dob ? 'error' : ''}`}>
                        <div className="grid-3">
                          <div className="field">
                            <select value={dobYear} onChange={e => setDobYear(e.target.value)}>
                              <option value="">Year</option>
                              {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                          </div>
                          <div className="field">
                            <select value={dobMonth} onChange={e => setDobMonth(e.target.value)}>
                              <option value="">Month</option>
                              {monthOptions.map(month => <option key={month} value={month}>{month}</option>)}
                            </select>
                          </div>
                          <div className="field">
                            <select value={dobDay} onChange={e => setDobDay(e.target.value)}>
                              <option value="">Day</option>
                              {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <label className="field-block">
                      <span>Health card number</span>
                      <input
                        type="text"
                        value={form.appointment_health_card}
                        onChange={(e) => update('appointment_health_card', e.target.value)}
                      />
                    </label>
                    <label className="field-block">
                      <span>Phone <span className="clinic-req">*</span></span>
                      <input
                        type="tel"
                        value={form.appointment_phone}
                        onChange={(e) => update('appointment_phone', e.target.value)}
                        className={errors.appointment_phone ? 'error-border' : ''}
                      />
                    </label>
                    <label className="field-block">
                      <span>Email</span>
                      <input
                        type="email"
                        value={form.appointment_email}
                        onChange={(e) => update('appointment_email', e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="selection-summary">&nbsp;</div>

                  <div
                    className={['terms-block', form.terms ? 'checked' : '', errors.terms ? 'error-border' : ''].join(' ')}
                  >
                    <div className="custom-check" onClick={() => update('terms', !form.terms)}>
                      {form.terms && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                          stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <div className="terms-text">
                      <div>
                        I hereby consent to the collection, use, and disclosure of my eye care information 
                        by {companyName} for the purpose of booking and managing my appointment, in accordance with 
                        the <a onClick={() => setShowTermsOfUse(showTermsOfUse ? false : true)}>Terms of Use</a> and <a onClick={() => setShowPrivacyPolicy(showPrivacyPolicy ? false : true)}>Privacy Policy</a>. 
                        This includes administrative and support services such as scheduling eye care appointments, managing 
                        patient records, issuing reminders, telephone communications, and processing applicable billing or provincial 
                        health insurance claims. I confirm that the information provided is accurate to the best of my knowledge.
                        <span className="clinic-req">*</span>
                      </div>
                      {showTermsOfUse && (
                        <Control.Form.Terms />
                      )}
                      {showPrivacyPolicy && (
                        <Control.Form.Privacy />
                      )}
                      
                    </div>
                    

                  </div>

                  {/* Anti-spam measures */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={(e) => update('website', e.target.value)}
                    style={{ display: 'none' }}
                    autoComplete="off"
                    tabIndex={-1}
                  />
                  <div className="recaptcha-container" style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <ReCAPTCHA
                      sitekey="6Lc8uG4qAAAAAHCmxZUJINB2c2wH13iuBCWOwr5k" // Replace with your actual Google reCAPTCHA site key
                      onChange={setRecaptchaToken}
                    />
                  </div>

                  <button type="submit" className="submit-btn" style={{backgroundColor:`#${buttonThemeColor}`}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                    </svg>
                    Book Appointment
                  </button>
                </>
              )}
              <div className="privacy-notice">
                🔒 Your information is encrypted and protected under HIPAA / PHIPA.<br />
                This form is for new and returning patients of {companyName}.
              </div>
              <div className="field-poweredby" style={{opacity : 1}}>
                <span>Powered by:</span> 
                <img src="/assets/images/basic/next-vizon.png" alt="NextVizon Optometry Software" /> 
                <span>Optometry Software</span>
              </div>
                
            </form>
          )}
        </div>
      </div>
      {toast && (
        <div className="toast-error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default AppointmentOnline;